import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Hammer, CheckCircle, ChevronRight, Plus,
  DollarSign, Camera, FileText, X,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import {
  INITIAL_PROJECTS,
  type EstimateStage,
  type InvoiceStatus,
  type Phase,
  type PhaseStatus,
  type Project,
  type ProjectStatus,
} from '../lib/operatorWorkspace'
import { cn } from '../lib/utils'

const ESTIMATE_STAGES: EstimateStage[] = ['Draft', 'Sent to GC', 'Estimate Received', 'Sent to Investor', 'Approved']

const PHASE_STATUS_STYLE: Record<PhaseStatus, string> = {
  Pending: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400',
  'In Progress': 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300',
  Complete: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300',
  Blocked: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300',
}

const INVOICE_STYLE: Record<InvoiceStatus, string> = {
  'Not Submitted': 'text-slate-400',
  'Pending Review': 'text-amber-600 dark:text-amber-400',
  Approved: 'text-blue-600 dark:text-blue-400',
  Paid: 'text-emerald-600 dark:text-emerald-400',
  Disputed: 'text-rose-600 dark:text-rose-400',
}

const PROJECT_STATUS_STYLE: Record<ProjectStatus, string> = {
  Estimate: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800',
  Approved: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/30 dark:text-violet-300 dark:border-violet-800',
  'In Progress': 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800',
  'Punch List': 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/30 dark:text-sky-300 dark:border-sky-800',
  Complete: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800',
  'On Hold': 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-600',
}

const STARTER_PHASES: Phase[] = [
  {
    id: 'phase-scope',
    name: 'Scope Walkthrough',
    trade: 'General',
    subcontractor: 'TBD',
    startDate: '2026-07-15',
    endDate: '2026-07-16',
    status: 'Pending',
    budget: 500,
    actual: 0,
    photos: 0,
    invoiceStatus: 'Not Submitted',
    invoiceAmount: 0,
    notes: 'Capture scope, budget, and trade requirements.',
  },
  {
    id: 'phase-bids',
    name: 'Bid Collection',
    trade: 'General',
    subcontractor: 'TBD',
    startDate: '2026-07-17',
    endDate: '2026-07-21',
    status: 'Pending',
    budget: 2500,
    actual: 0,
    photos: 0,
    invoiceStatus: 'Not Submitted',
    invoiceAmount: 0,
    notes: 'Collect at least three bids and validate line items.',
  },
]

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n)

// ─── Estimate Workflow ────────────────────────────────────────────────────────

function EstimateFlow({ project, onAdvance }: { project: Project; onAdvance: (stage: EstimateStage) => void }) {
  const stageIdx = ESTIMATE_STAGES.indexOf(project.estimateStage)
  const isApproved = project.estimateStage === 'Approved'
  const isRejected = project.estimateStage === 'Rejected'

  const stageActions: Partial<Record<EstimateStage, { label: string; next: EstimateStage; color?: string }>> = {
    Draft: { label: 'Send to GC for Estimate', next: 'Sent to GC' },
    'Sent to GC': { label: 'Mark Estimate Received', next: 'Estimate Received' },
    'Estimate Received': { label: 'Forward to Investor', next: 'Sent to Investor' },
    'Sent to Investor': { label: 'Mark Approved', next: 'Approved', color: 'bg-emerald-600 hover:bg-emerald-700' },
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Estimate Approval Workflow</p>
        <div className="flex flex-wrap items-center gap-0">
          {ESTIMATE_STAGES.map((stage, i) => (
            <div key={stage} className="flex items-center">
              <div className={cn('flex flex-col items-center')}>
                <div className={cn(
                  'h-8 w-8 rounded-full border-2 flex items-center justify-center text-xs font-bold transition-all',
                  i < stageIdx ? 'border-violet-600 bg-violet-600 text-white' :
                  i === stageIdx ? 'border-violet-600 bg-white dark:bg-slate-900 text-violet-600' :
                  'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-400'
                )}>
                  {i < stageIdx ? <CheckCircle className="h-4 w-4" /> : <span>{i + 1}</span>}
                </div>
                <p className={cn('mt-1 text-center text-[10px] font-medium max-w-[70px] leading-tight hidden sm:block',
                  i <= stageIdx ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'
                )}>{stage}</p>
              </div>
              {i < ESTIMATE_STAGES.length - 1 && (
                <div className={cn('h-0.5 w-6 sm:w-10 mx-0.5 mb-4', i < stageIdx ? 'bg-violet-600' : 'bg-slate-200 dark:bg-slate-700')} />
              )}
            </div>
          ))}
        </div>

        {!isApproved && !isRejected && stageActions[project.estimateStage] && (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              size="sm"
              className={cn('text-white', stageActions[project.estimateStage]?.color ?? 'bg-violet-600 hover:bg-violet-700')}
              onClick={() => onAdvance(stageActions[project.estimateStage]!.next)}
            >
              {stageActions[project.estimateStage]?.label}
            </Button>
            {project.estimateStage === 'Sent to Investor' && (
              <Button size="sm" variant="secondary" className="text-rose-600" onClick={() => onAdvance('Rejected')}>
                Reject
              </Button>
            )}
          </div>
        )}

        {isApproved && (
          <div className="mt-3 flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300 font-semibold">
            <CheckCircle className="h-4 w-4" /> Estimate approved — project is active
          </div>
        )}
        {isRejected && (
          <div className="mt-3 flex items-center gap-2 text-sm text-rose-700 dark:text-rose-300 font-semibold">
            <X className="h-4 w-4" /> Estimate rejected — revision requested
          </div>
        )}
      </div>

      {/* Line-item estimate */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="bg-slate-50 dark:bg-slate-800/60 px-4 py-2.5 flex items-center justify-between">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Line-Item Estimate</p>
          <span className="text-xs font-bold text-violet-700 dark:text-violet-300">{fmt(project.estimatedCost)} total</span>
        </div>
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {project.phases.map(ph => (
            <div key={ph.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
              <div>
                <p className="font-medium text-slate-900 dark:text-slate-100">{ph.name}</p>
                <p className="text-xs text-slate-500">{ph.trade} · {ph.subcontractor === 'TBD' ? <span className="text-amber-600">Sub TBD</span> : ph.subcontractor}</p>
              </div>
              <p className="font-semibold text-slate-900 dark:text-slate-100">{fmt(ph.budget)}</p>
            </div>
          ))}
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-800/60 font-bold">
            <span className="text-slate-700 dark:text-slate-200">Total Estimate</span>
            <span className="text-violet-700 dark:text-violet-300">{fmt(project.estimatedCost)}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Phase Timeline ───────────────────────────────────────────────────────────

function PhaseTimeline({ project, onPhaseAction }: {
  project: Project
  onPhaseAction: (phaseId: string, action: 'complete' | 'upload' | 'submit-invoice' | 'pay') => void
}) {
  const completedPhases = project.phases.filter(p => p.status === 'Complete').length
  const progressPct = Math.round((completedPhases / project.phases.length) * 100)
  const totalBudget = project.phases.reduce((s, p) => s + p.budget, 0)
  const totalActual = project.phases.reduce((s, p) => s + p.actual, 0)
  const pendingInvoices = project.phases.filter(p => p.invoiceStatus === 'Pending Review').length

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
        {[
          { label: 'Phases Done', value: `${completedPhases}/${project.phases.length}`, sub: `${progressPct}% complete` },
          { label: 'Budget Used', value: fmt(totalActual), sub: `of ${fmt(totalBudget)}` },
          { label: 'Pending Invoices', value: String(pendingInvoices), sub: pendingInvoices > 0 ? 'Needs review' : 'All clear' },
          { label: 'Remaining Budget', value: fmt(Math.max(0, totalBudget - totalActual)), sub: 'Est. remaining' },
        ].map(c => (
          <Card key={c.label} className="p-3 shadow-sm">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{c.label}</p>
            <p className="mt-1 text-xl font-bold text-slate-900 dark:text-slate-50">{c.value}</p>
            <p className="text-xs text-slate-500 mt-0.5">{c.sub}</p>
          </Card>
        ))}
      </div>

      <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
        <motion.div className="h-full rounded-full bg-violet-600" initial={{ width: 0 }}
          animate={{ width: `${progressPct}%` }} transition={{ duration: 0.8 }} />
      </div>

      <div className="space-y-2">
        {project.phases.map((phase, i) => (
          <div key={phase.id} className={cn(
            'rounded-xl border p-3 transition-all',
            phase.status === 'In Progress' ? 'border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/10' :
            phase.status === 'Complete' ? 'border-emerald-200 bg-emerald-50/30 dark:border-emerald-800 dark:bg-emerald-950/10' :
            phase.status === 'Blocked' ? 'border-rose-200 bg-rose-50/30 dark:border-rose-800 dark:bg-rose-950/10' :
            'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'
          )}>
            <div className="flex flex-wrap items-start gap-3">
              <div className={cn(
                'h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5',
                phase.status === 'Complete' ? 'bg-emerald-600 text-white' :
                phase.status === 'In Progress' ? 'bg-amber-500 text-white' :
                phase.status === 'Blocked' ? 'bg-rose-500 text-white' :
                'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
              )}>
                {phase.status === 'Complete' ? <CheckCircle className="h-4 w-4" /> : i + 1}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-bold text-slate-900 dark:text-slate-100">{phase.name}</p>
                  <span className={cn('rounded-full border px-2 py-0.5 text-[10px] font-bold', PHASE_STATUS_STYLE[phase.status])}>{phase.status}</span>
                  {phase.subcontractor !== 'TBD' && (
                    <span className="text-[10px] text-slate-500 bg-slate-100 dark:bg-slate-800 rounded-full px-2 py-0.5">{phase.subcontractor}</span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-slate-500">
                  <span>{phase.trade}</span>
                  <span>·</span>
                  <span>{phase.startDate} → {phase.endDate}</span>
                  <span>·</span>
                  <span>Budget: <strong className="text-slate-700 dark:text-slate-200">{fmt(phase.budget)}</strong></span>
                  {phase.actual > 0 && <span>· Actual: <strong className="text-slate-700 dark:text-slate-200">{fmt(phase.actual)}</strong></span>}
                  {phase.photos > 0 && (
                    <span className="flex items-center gap-1 text-violet-600 dark:text-violet-400">
                      <Camera className="h-3 w-3" />{phase.photos} photos
                    </span>
                  )}
                </div>
                {phase.notes && <p className="text-[11px] text-slate-400 mt-1 italic">{phase.notes}</p>}

                {/* Invoice status */}
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <span className={cn('text-[11px] font-semibold flex items-center gap-1', INVOICE_STYLE[phase.invoiceStatus])}>
                    <FileText className="h-3 w-3" />
                    Invoice: {phase.invoiceStatus}
                    {phase.invoiceAmount > 0 && ` · ${fmt(phase.invoiceAmount)}`}
                  </span>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-wrap gap-1.5 shrink-0">
                {phase.status === 'In Progress' && (
                  <>
                    <Button size="sm" variant="secondary" className="h-7 px-2 text-[11px]"
                      onClick={() => onPhaseAction(phase.id, 'upload')}>
                      <Camera className="mr-1 h-3 w-3" />Photos
                    </Button>
                    {phase.invoiceStatus === 'Not Submitted' && (
                      <Button size="sm" variant="secondary" className="h-7 px-2 text-[11px]"
                        onClick={() => onPhaseAction(phase.id, 'submit-invoice')}>
                        <FileText className="mr-1 h-3 w-3" />Invoice
                      </Button>
                    )}
                    <Button size="sm" className="h-7 px-2 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => onPhaseAction(phase.id, 'complete')}>
                      <CheckCircle className="mr-1 h-3 w-3" />Complete
                    </Button>
                  </>
                )}
                {phase.invoiceStatus === 'Pending Review' && (
                  <Button size="sm" className="h-7 px-2 text-[11px] bg-violet-600 hover:bg-violet-700 text-white"
                    onClick={() => onPhaseAction(phase.id, 'pay')}>
                    <DollarSign className="mr-1 h-3 w-3" />Approve & Pay
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function ConstructionKanban({
  project,
  onMovePhase,
}: {
  project: Project
  onMovePhase: (phaseId: string, status: PhaseStatus) => void
}) {
  const columns: Array<{ status: PhaseStatus; title: string }> = [
    { status: 'Pending', title: 'Queued' },
    { status: 'In Progress', title: 'In Progress' },
    { status: 'Blocked', title: 'Blocked' },
    { status: 'Complete', title: 'Done' },
  ]

  return (
    <div className="space-y-3">
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Construction Kanban</CardTitle>
          <p className="text-xs text-slate-500">n8n-style operational board for phase-by-phase progress visibility.</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-4">
            {columns.map((column) => {
              const phases = project.phases.filter((phase) => phase.status === column.status)
              return (
                <div key={column.status} className="rounded-xl border border-slate-200 bg-slate-50 p-2.5 dark:border-slate-700 dark:bg-slate-900/60">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">{column.title}</p>
                    <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">{phases.length}</span>
                  </div>
                  <div className="space-y-2">
                    {phases.map((phase) => (
                      <div key={phase.id} className="rounded-lg border border-white/70 bg-white p-2.5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{phase.name}</p>
                        <p className="text-xs text-slate-500">{phase.trade} · {phase.subcontractor}</p>
                        <div className="mt-1 flex items-center justify-between text-xs text-slate-500">
                          <span>{fmt(phase.budget)}</span>
                          <span>{phase.startDate} → {phase.endDate}</span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {phase.status !== 'Pending' && (
                            <Button size="sm" variant="secondary" className="h-6 px-2 text-[10px]" onClick={() => onMovePhase(phase.id, 'Pending')}>Queue</Button>
                          )}
                          {phase.status !== 'In Progress' && (
                            <Button size="sm" variant="secondary" className="h-6 px-2 text-[10px]" onClick={() => onMovePhase(phase.id, 'In Progress')}>Start</Button>
                          )}
                          {phase.status !== 'Blocked' && (
                            <Button size="sm" variant="secondary" className="h-6 px-2 text-[10px] text-rose-600" onClick={() => onMovePhase(phase.id, 'Blocked')}>Block</Button>
                          )}
                          {phase.status !== 'Complete' && (
                            <Button size="sm" className="h-6 px-2 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onMovePhase(phase.id, 'Complete')}>Done</Button>
                          )}
                        </div>
                      </div>
                    ))}
                    {phases.length === 0 && (
                      <div className="rounded-lg border border-dashed border-slate-300 px-2.5 py-4 text-center text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">No phases</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ─── Project Card ─────────────────────────────────────────────────────────────

function ProjectCard({ project, onSelect }: { project: Project; onSelect: () => void }) {
  const done = project.phases.filter(p => p.status === 'Complete').length
  const pct = Math.round((done / project.phases.length) * 100)
  const pendingPay = project.phases.filter(p => p.invoiceStatus === 'Pending Review').length

  return (
    <button data-testid={`construction-project-${project.id}`} onClick={onSelect} className="w-full text-left">
      <Card className="p-4 shadow-sm hover:shadow-md transition-shadow border-l-4"
        style={{ borderLeftColor: project.status === 'In Progress' ? '#f59e0b' : project.status === 'Complete' ? '#10b981' : '#6366f1' }}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="font-bold text-slate-900 dark:text-slate-100">{project.name}</p>
            <p className="text-xs text-slate-500 mt-0.5">{project.address}</p>
            <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
              <span className="text-slate-500">PM: <span className="font-medium text-slate-700 dark:text-slate-200">{project.pm}</span></span>
              <span className="text-slate-300">·</span>
              <span className="text-slate-500">GC: <span className="font-medium text-slate-700 dark:text-slate-200">{project.gc}</span></span>
              <span className="text-slate-300">·</span>
              <span className="font-semibold text-violet-700 dark:text-violet-300">{fmt(project.estimatedCost)}</span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <span className={cn('rounded-full border px-2.5 py-0.5 text-[11px] font-bold', PROJECT_STATUS_STYLE[project.status])}>{project.status}</span>
            {pendingPay > 0 && (
              <span className="rounded-full border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 px-2 py-0.5 text-[10px] font-bold text-amber-700 dark:text-amber-300">
                {pendingPay} invoice{pendingPay > 1 ? 's' : ''} pending
              </span>
            )}
          </div>
        </div>
        {project.status === 'In Progress' && (
          <div className="mt-3">
            <div className="flex justify-between text-[11px] text-slate-500 mb-1">
              <span>Phases: {done}/{project.phases.length}</span>
              <span>{pct}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
              <div className="h-full rounded-full bg-violet-600 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
        {project.estimateStage !== 'Approved' && (
          <div className="mt-2 text-[11px] text-slate-500">
            Estimate: <span className="font-semibold text-blue-600 dark:text-blue-400">{project.estimateStage}</span>
          </div>
        )}
        <div className="mt-2 flex items-center gap-1 text-xs text-slate-400">
          <ChevronRight className="h-3.5 w-3.5" />
          <span>Click to view phases & workflow</span>
        </div>
      </Card>
    </button>
  )
}

// ─── Project Detail ───────────────────────────────────────────────────────────

function ProjectDetail({ project, onBack, onEstimateAdvance, onPhaseAction, onMessage, onMovePhaseStatus }: {
  project: Project; onBack: () => void
  onEstimateAdvance: (stage: EstimateStage) => void
  onPhaseAction: (phaseId: string, action: 'complete' | 'upload' | 'submit-invoice' | 'pay') => void
  onMessage: (name: string) => void
  onMovePhaseStatus: (phaseId: string, status: PhaseStatus) => void
}) {
  const [tab, setTab] = useState<'workflow' | 'phases' | 'kanban' | 'team'>('phases')

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="secondary" size="sm" onClick={onBack} className="h-8 px-3 text-xs">← Back</Button>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 truncate">{project.name}</h2>
          <p className="text-xs text-slate-500">{project.address}</p>
        </div>
        <span className={cn('rounded-full border px-2.5 py-1 text-xs font-bold shrink-0', PROJECT_STATUS_STYLE[project.status])}>{project.status}</span>
      </div>

      <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1">
        {([['phases', 'Phase Timeline'], ['kanban', 'Kanban Board'], ['workflow', 'Estimate Workflow'], ['team', 'Team']] as const).map(([id, label]) => (
          <button key={id} data-testid={`project-tab-${id}`} onClick={() => setTab(id)}
            className={cn('flex-1 rounded-lg px-2 py-2 text-xs font-medium transition-all text-center',
              tab === id ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm' : 'text-slate-600 dark:text-slate-400'
            )}>{label}</button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={tab} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}>
          {tab === 'phases' && <PhaseTimeline project={project} onPhaseAction={onPhaseAction} />}
          {tab === 'kanban' && <ConstructionKanban project={project} onMovePhase={onMovePhaseStatus} />}
          {tab === 'workflow' && <EstimateFlow project={project} onAdvance={onEstimateAdvance} />}
          {tab === 'team' && (
            <Card className="shadow-sm">
              <CardContent className="p-4 space-y-3">
                {[
                  { role: 'Property Manager', name: project.pm, badge: 'PM' },
                  { role: 'General Contractor', name: project.gc, badge: 'GC' },
                  { role: 'Investor / Owner', name: project.investor, badge: 'INV' },
                  ...Array.from(new Set(project.phases.filter(p => p.subcontractor !== 'TBD').map(p => p.subcontractor))).map(s => ({
                    role: project.phases.find(p => p.subcontractor === s)?.trade ?? 'Sub',
                    name: s, badge: 'SUB',
                  })),
                ].map((m, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/40">
                    <div className="h-9 w-9 rounded-full bg-violet-600 flex items-center justify-center text-[10px] font-bold text-white shrink-0">{m.badge}</div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{m.name}</p>
                      <p className="text-xs text-slate-500">{m.role}</p>
                    </div>
                    <Button size="sm" variant="secondary" className="ml-auto h-7 px-2.5 text-xs" onClick={() => onMessage(m.name)}>Message</Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

// ─── Invoices Tab ─────────────────────────────────────────────────────────────

function InvoicesTab({ projects, onPay }: { projects: Project[]; onPay: (projectId: string, phaseId: string) => void }) {
  const allInvoices = projects.flatMap(p =>
    p.phases
      .filter(ph => ph.invoiceStatus !== 'Not Submitted')
      .map(ph => ({ ...ph, projectName: p.name, projectId: p.id }))
  )

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        {[
          { label: 'Pending Review', value: allInvoices.filter(i => i.invoiceStatus === 'Pending Review').length, tone: 'amber' },
          { label: 'Approved', value: allInvoices.filter(i => i.invoiceStatus === 'Approved').length, tone: 'blue' },
          { label: 'Paid This Month', value: allInvoices.filter(i => i.invoiceStatus === 'Paid').length, tone: 'green' },
          { label: 'Pending Amount', value: fmt(allInvoices.filter(i => i.invoiceStatus === 'Pending Review').reduce((s, i) => s + i.invoiceAmount, 0)), tone: 'amber' },
        ].map(c => (
          <Card key={c.label} className="p-3 shadow-sm">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{c.label}</p>
            <p className="mt-1.5 text-2xl font-bold text-slate-900 dark:text-slate-50 leading-none">{c.value}</p>
          </Card>
        ))}
      </div>

      <Card className="shadow-sm">
        <CardHeader><CardTitle className="text-base">Invoice Queue</CardTitle></CardHeader>
        <CardContent className="p-0">
          {allInvoices.length === 0 ? (
            <p className="text-center py-8 text-sm text-slate-400">No invoices submitted yet</p>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {allInvoices.map(inv => (
                <div key={`${inv.projectId}-${inv.id}`} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{inv.name}</p>
                    <p className="text-xs text-slate-500">{inv.projectName} · {inv.subcontractor} · {inv.trade}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="font-bold text-slate-900 dark:text-slate-100">{fmt(inv.invoiceAmount)}</p>
                    <span className={cn('rounded-full border px-2.5 py-0.5 text-[11px] font-bold',
                      inv.invoiceStatus === 'Paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300' :
                      inv.invoiceStatus === 'Pending Review' ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300' :
                      inv.invoiceStatus === 'Approved' ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300' :
                      'bg-rose-50 text-rose-700 border-rose-200'
                    )}>{inv.invoiceStatus}</span>
                    {(inv.invoiceStatus === 'Pending Review' || inv.invoiceStatus === 'Approved') && (
                      <Button size="sm" className="h-7 px-3 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => onPay(inv.projectId, inv.id)}>
                        Pay {fmt(inv.invoiceAmount)}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// ─── Main Export ──────────────────────────────────────────────────────────────

type ConstructionTab = 'projects' | 'invoices'

type ConstructionModuleProps = {
  projects?: Project[]
  onProjectsChange?: (projects: Project[]) => void
}

export default function ConstructionModule({ projects: controlledProjects, onProjectsChange }: ConstructionModuleProps) {
  const [internalProjects, setInternalProjects] = useState<Project[]>(INITIAL_PROJECTS)
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const [tab, setTab] = useState<ConstructionTab>('projects')
  const [notice, setNotice] = useState<string | null>(null)
  const projects = controlledProjects ?? internalProjects

  const updateProjects = (next: Project[] | ((prev: Project[]) => Project[])) => {
    const resolved = typeof next === 'function' ? next(projects) : next
    if (onProjectsChange) {
      onProjectsChange(resolved)
      return
    }
    setInternalProjects(resolved)
  }

  const selectedProject = projects.find(p => p.id === selectedProjectId)

  const advanceEstimate = (stage: EstimateStage) => {
    updateProjects(prev => prev.map(p =>
      p.id === selectedProjectId ? { ...p, estimateStage: stage, status: stage === 'Approved' ? 'In Progress' : p.status } : p
    ))
  }

  const handlePhaseAction = (phaseId: string, action: 'complete' | 'upload' | 'submit-invoice' | 'pay') => {
    updateProjects(prev => prev.map(p => {
      if (p.id !== selectedProjectId) return p
      return {
        ...p,
        phases: p.phases.map(ph => {
          if (ph.id !== phaseId) return ph
          if (action === 'complete') return { ...ph, status: 'Complete' as PhaseStatus }
          if (action === 'upload') return { ...ph, photos: ph.photos + 1 }
          if (action === 'submit-invoice') return { ...ph, invoiceStatus: 'Pending Review' as InvoiceStatus, invoiceAmount: ph.budget, status: 'In Progress' as PhaseStatus }
          if (action === 'pay') return { ...ph, invoiceStatus: 'Paid' as InvoiceStatus, actual: ph.invoiceAmount }
          return ph
        }),
      }
    }))
  }

  const handlePay = (projectId: string, phaseId: string) => {
    updateProjects(prev => prev.map(p => {
      if (p.id !== projectId) return p
      return { ...p, phases: p.phases.map(ph => ph.id === phaseId ? { ...ph, invoiceStatus: 'Paid' as InvoiceStatus, actual: ph.invoiceAmount } : ph) }
    }))
  }

  const handleMovePhaseStatus = (phaseId: string, status: PhaseStatus) => {
    updateProjects((prev) =>
      prev.map((project) => {
        if (project.id !== selectedProjectId) {
          return project
        }
        return {
          ...project,
          phases: project.phases.map((phase) => (phase.id === phaseId ? { ...phase, status } : phase)),
        }
      }),
    )
  }

  const activeCount = projects.filter(p => p.status === 'In Progress').length
  const pendingInvoicesCount = projects.flatMap(p => p.phases).filter(ph => ph.invoiceStatus === 'Pending Review').length
  const totalBudget = projects.reduce((s, p) => s + p.approvedBudget + p.estimatedCost, 0) / 2

  const handleNewProject = () => {
    const projectIndex = projects.length + 1
    const projectId = `project-${Date.now()}`
    const phasePrefix = `${projectId}-phase`
    const newProject: Project = {
      id: projectId,
      name: `New Rehab Project ${projectIndex}`,
      address: 'Set property address',
      type: 'BRRR',
      status: 'Estimate',
      estimateStage: 'Draft',
      pm: 'Assign PM',
      gc: 'Assign contractor',
      investor: 'Assign investor',
      estimatedCost: 18500,
      approvedBudget: 0,
      actualCost: 0,
      startDate: '2026-07-15',
      completionDate: '2026-09-01',
      phases: STARTER_PHASES.map((phase, index) => ({
        ...phase,
        id: `${phasePrefix}-${index + 1}`,
      })),
    }

    updateProjects((prev) => [newProject, ...prev])
    setSelectedProjectId(newProject.id)
    setNotice(`Created ${newProject.name}. Update scope, address, and estimate details to start intake.`)
  }

  return (
    <div className="space-y-4">
      {notice && (
        <Card className="p-3 shadow-sm border-emerald-200 bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/25">
          <p className="text-xs text-emerald-800 dark:text-emerald-300">{notice}</p>
        </Card>
      )}

      {!selectedProject && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">Construction Management</h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">Full project lifecycle: estimate → approve → schedule → pay</p>
            </div>
            <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" onClick={handleNewProject}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />New Project
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            {[
              { label: 'Active Projects', value: String(activeCount) },
              { label: 'In Estimate', value: String(projects.filter(p => p.status === 'Estimate').length) },
              { label: 'Pending Invoices', value: String(pendingInvoicesCount), alert: pendingInvoicesCount > 0 },
              { label: 'Total Budget', value: fmt(totalBudget) },
            ].map(c => (
              <Card key={c.label} className={cn('p-3 shadow-sm', c.alert && 'border-amber-200 bg-amber-50/40 dark:border-amber-800')}>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{c.label}</p>
                <p className={cn('mt-1.5 text-2xl font-bold leading-none', c.alert ? 'text-amber-700 dark:text-amber-400' : 'text-slate-900 dark:text-slate-50')}>{c.value}</p>
              </Card>
            ))}
          </div>

          <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1">
            <button onClick={() => setTab('projects')} className={cn('flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all', tab === 'projects' ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm' : 'text-slate-600 dark:text-slate-400 hover:bg-white/60')}>
              <Hammer className="h-4 w-4" />Projects
            </button>
            <button onClick={() => setTab('invoices')} className={cn('flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all', tab === 'invoices' ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm' : 'text-slate-600 dark:text-slate-400 hover:bg-white/60')}>
              <DollarSign className="h-4 w-4" />Invoices & Payments
              {pendingInvoicesCount > 0 && <span className="ml-1 h-4 w-4 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center">{pendingInvoicesCount}</span>}
            </button>
          </div>

          <motion.div key={tab} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}>
            {tab === 'projects' && (
              <div className="space-y-3">
                {projects.map(p => <ProjectCard key={p.id} project={p} onSelect={() => setSelectedProjectId(p.id)} />)}
              </div>
            )}
            {tab === 'invoices' && <InvoicesTab projects={projects} onPay={handlePay} />}
          </motion.div>
        </>
      )}

      {selectedProject && (
        <ProjectDetail
          project={selectedProject}
          onBack={() => setSelectedProjectId(null)}
          onEstimateAdvance={advanceEstimate}
          onPhaseAction={handlePhaseAction}
          onMessage={(name) => setNotice(`Message workflow started for ${name}.`) }
          onMovePhaseStatus={handleMovePhaseStatus}
        />
      )}
    </div>
  )
}
